import {mapIncludedSegmentGroups} from '@/utils/rewardPayloadUtils';

const isNotNull = (val) => val !== null && val !== undefined;

const isNonEmptyArray = (arr) => Array.isArray(arr) && arr.length > 0;

const isValidValue = (val) => val !== '' && val !== null && val !== undefined;

/**
 * True when any string value in the (possibly nested) object is non-empty.
 */
export const hasAnyNonEmptyValue = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  return Object.values(obj).some((v) => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (typeof v === 'object') return hasAnyNonEmptyValue(v);
    return true;
  });
};

/**
 * Transform UI quiz questions structure to API format.
 */
export const buildQuizQuestionsList = (wofQuizQuestions) => {
  if (
    !wofQuizQuestions ||
    !Array.isArray(wofQuizQuestions.data) ||
    wofQuizQuestions.data.length === 0
  ) {
    return [];
  }

  return wofQuizQuestions.data.map((item) => ({
    id: item.id,
    translations: item.translations
  }));
};

/**
 * Transform API quiz questions structure to UI format.
 */
export const mapQuizQuestionsListToUI = (quizQuestions, fileMeta = {}) => {
  if (!Array.isArray(quizQuestions) || quizQuestions.length === 0) {
    return null;
  }

  return {
    data: quizQuestions.map((item) => ({
      id: item.id,
      translations: item.translations
    })),
    quizFileName: fileMeta.quizFileName || fileMeta.fileName || '',
    quizFileUrl: fileMeta.quizFileUrl || '',
    updatedAt: fileMeta.updatedAt || '',
    fileName: fileMeta.quizFileName || fileMeta.fileName || '',
    lastUpdate: fileMeta.updatedAt || ''
  };
};

/** Map API excluded list to SegmentsCard / form `{ label, value }[]`. */
export const mapApiExcludedSegmentsToForm = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.map((s) => {
    if (typeof s === 'string') {
      return {label: s, value: s};
    }
    const segmentId = String(s?.segmentId ?? s?.value ?? s?.id ?? '');
    const label = String(s?.segmentName ?? s?.label ?? segmentId);
    return {label, value: segmentId};
  });
};

export const excludedFieldsForReviewOrder = (processedData) => {
  const {excludedSegments, segmentExclude} = processedData;
  return {
    ...(Array.isArray(excludedSegments) && excludedSegments.length > 0
      ? {excludedSegments}
      : {}),
    ...(segmentExclude !== undefined ? {segmentExclude} : {})
  };
};

/**
 * Pull WOF / FreeKick displayConfig fields back into flat form fields.
 * @returns {Object} merged displayConfig-derived values
 */
export const extractDisplayConfigFields = (displayConfig, current) => {
  if (!displayConfig || typeof displayConfig !== 'object') {
    return {};
  }

  const result = {...current};

  if (result.dailyDisplayStartTime == null && displayConfig.startTime != null) {
    result.dailyDisplayStartTime = displayConfig.startTime;
  }
  if (result.dailyDisplayEndTime == null && displayConfig.endTime != null) {
    result.dailyDisplayEndTime = displayConfig.endTime;
  }
  if (
    (!Array.isArray(result.daysOfWeekUnavailable) ||
      result.daysOfWeekUnavailable.length === 0) &&
    Array.isArray(displayConfig.daysOfTheWeekUnavailable)
  ) {
    result.daysOfWeekUnavailable = displayConfig.daysOfTheWeekUnavailable;
  }
  if (result.dailyPlayLimit == null && displayConfig.dailyLimit != null) {
    result.dailyPlayLimit = displayConfig.dailyLimit;
  }

  const apiBudgetPeriod = displayConfig.gameConfig?.budget?.frequency;
  if (
    (result.budgetPeriod == null || result.budgetPeriod === '') &&
    apiBudgetPeriod != null
  ) {
    result.budgetPeriod = apiBudgetPeriod
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  const apiTotalPoints = displayConfig.gameConfig?.budget?.amount;
  if (
    (result.pointsEarned == null ||
      result.pointsEarned === '' ||
      result.pointsEarned === 0) &&
    apiTotalPoints != null
  ) {
    result.pointsEarned = String(apiTotalPoints);
  }

  result.equalProbability =
    displayConfig.gameConfig?.equalProbabilityPerSlice ?? undefined;
  result.equalPoints =
    displayConfig.gameConfig?.equalPointsPerSlice ?? undefined;

  return result;
};

/**
 * Coerce numeric API fields to strings for form consumption.
 * Also maps pointsToRedeem / localCurrencyValue fallbacks.
 */
const resolveStringOrFallback = (primary, fallback) => {
  if (primary !== null && primary !== undefined) return String(primary);
  if (fallback !== null && fallback !== undefined) return String(fallback);
  return undefined;
};

const coerceToString = (val) => {
  if (val) return String(val);
  return val;
};

export const coerceNumericFields = (src) => {
  return {
    pointsEarned: coerceToString(src?.pointsEarned),
    numberOfCases: coerceToString(src?.numberOfCases),
    receivePoints: coerceToString(src?.receivePoints),
    noOfDays:
      src?.noOfDays !== null && src?.noOfDays !== undefined
        ? String(src.noOfDays)
        : undefined,
    allTimeLoginLimit:
      src?.allTimeLoginLimit !== null &&
      src?.allTimeLoginLimit !== undefined &&
      src?.allTimeLoginLimit !== ''
        ? String(src.allTimeLoginLimit)
        : undefined,
    allTimeLimit:
      src?.allTimeLimit !== null &&
      src?.allTimeLimit !== undefined &&
      src?.allTimeLimit !== ''
        ? String(src.allTimeLimit)
        : undefined,
    pointsToRedeem: resolveStringOrFallback(
      src?.pointsToRedeem,
      src?.pointsEarned
    ),
    localCurrencyValue: resolveStringOrFallback(
      src?.localCurrencyValue,
      src?.localCurrency
    ),
    pointsForSuccessfulUpload: coerceToString(src?.pointsForSuccessfulUpload),
    pointsPerLineItem: coerceToString(src?.pointsPerLineItem),
    photoOrderModalDisplayLimit:
      src?.photoOrderModalDisplayLimit !== null &&
      src?.photoOrderModalDisplayLimit !== undefined
        ? Number(src.photoOrderModalDisplayLimit)
        : undefined
  };
};

/**
 * Normalize included segments from the various API shapes.
 */
export const normalizeSegmentsFromApi = (src) => {
  const rawIncluded = src?.includedSegmentGroups;
  const rawSegments = src?.selectedSegments ?? src?.segments ?? src?.Segments;

  if (Array.isArray(rawIncluded) && rawIncluded.length > 0) {
    return mapIncludedSegmentGroups(rawIncluded);
  }

  if (Array.isArray(rawSegments) && rawSegments.length > 0) {
    const flatIds = rawSegments
      .map((s) =>
        typeof s === 'string' ? s : s?.id || s?.segmentName || s?.value
      )
      .filter(Boolean);
    return flatIds.length > 0 ? [flatIds] : undefined;
  }

  return undefined;
};

/**
 * Build the final preprocessed result object.
 */
export const buildPreprocessedResult = (fields) => {
  const result = {
    activityType: fields.activityType,
    pointsEarned: fields.pointsEarned,
    hasEndDate: fields.hasEndDate,
    startDate: fields.startDate,
    endDate: fields.endDate,
    dailyLoginLimit: fields.dailyLoginLimit,
    allTimeLoginLimit: fields.allTimeLoginLimit,
    activityName: fields.activityName,
    orderType: fields.orderType,
    localCurrency: fields.localCurrency,
    numberOfCases: fields.numberOfCases,
    noOfDays: fields.noOfDays,
    receivePoints: fields.receivePoints,
    status: fields.status,
    uuid: fields.uuid,
    id: fields.id,
    activityId: fields.activityId,
    selectedSegments: fields.selectedSegments,
    allCustomers: fields.allCustomers,
    pointsToRedeem: fields.pointsToRedeem,
    localCurrencyValue: fields.localCurrencyValue,
    displayOrder: fields.displayOrder,
    customerType: Array.isArray(fields.customerType) ? fields.customerType : [],
    url: fields.url ?? '',
    translations: fields.translations ?? {},
    mediaUrls: fields.mediaUrls ?? [],
    budgetPeriod: fields.budgetPeriod,
    dailyDisplayStartTime: fields.dailyDisplayStartTime,
    dailyDisplayEndTime: fields.dailyDisplayEndTime,
    daysOfWeekUnavailable: fields.daysOfWeekUnavailable,
    dailyPlayLimit: fields.dailyPlayLimit,
    allTimeLimit: fields.allTimeLimit,
    pointsForSuccessfulUpload: fields.pointsForSuccessfulUpload,
    pointsPerLineItem: fields.pointsPerLineItem,
    photoOrderModalDisplayLimit: fields.photoOrderModalDisplayLimit
  };

  if (fields.segmentExclude !== undefined) {
    result.segmentExclude = fields.segmentExclude;
  }
  if (fields.excludedSegments) {
    result.excludedSegments = fields.excludedSegments;
  }
  if (fields.displayConfig) {
    result.displayConfig = fields.displayConfig;
  }
  if (fields.translations) {
    result.translations = fields.translations;
  }
  if (fields.leaderboardRewardsList?.length > 0) {
    result.leaderboardRewardsList = fields.leaderboardRewardsList;
  }
  if (fields.quizQuestions?.length > 0) {
    result.quizQuestions = fields.quizQuestions;
  }
  if (fields.equalProbability !== undefined) {
    result.equalProbability = fields.equalProbability;
  }
  if (fields.equalPoints !== undefined) {
    result.equalPoints = fields.equalPoints;
  }

  return result;
};

/**
 * Build the optional fields object for WOF activity order.
 */
export const buildWofOptionalFields = (processedData) => {
  const {
    id,
    activityId,
    quizQuestions,
    activityName,
    uuid,
    status,
    budgetPeriod,
    dailyDisplayStartTime,
    dailyDisplayEndTime,
    daysOfWeekUnavailable,
    dailyPlayLimit,
    displayConfig,
    translations,
    equalProbability,
    equalPoints,
    selectedSegments,
    allCustomers
  } = processedData;

  const wofQuizQuestions = mapQuizQuestionsListToUI(quizQuestions);

  const entries = [
    ['activityName', activityName],
    ['uuid', uuid],
    ['status', status],
    ['id', id || activityId],
    ['budgetPeriod', budgetPeriod],
    ['dailyDisplayStartTime', dailyDisplayStartTime],
    ['dailyDisplayEndTime', dailyDisplayEndTime],
    ['displayConfig', displayConfig],
    ['translations', translations],
    ['equalProbability', equalProbability],
    ['equalPoints', equalPoints],
    ['wofQuizQuestions', wofQuizQuestions],
    ['allCustomers', allCustomers]
  ];

  if (isNonEmptyArray(daysOfWeekUnavailable)) {
    entries.push(['daysOfWeekUnavailable', daysOfWeekUnavailable]);
  }
  if (isNotNull(dailyPlayLimit)) {
    entries.push(['dailyPlayLimit', dailyPlayLimit]);
  }
  if (isNonEmptyArray(selectedSegments)) {
    entries.push(['selectedSegments', selectedSegments]);
  }

  const result = Object.fromEntries(
    entries.filter(([, val]) => isValidValue(val))
  );

  return {...result, ...excludedFieldsForReviewOrder(processedData)};
};

/**
 * Build the WOF-specific draft payload from generic base draft.
 */
export const buildWheelOfFortuneDraft = (activityFormData, baseDraft) => {
  const {
    pointsEarned,
    budgetPeriod,
    dailyDisplayStartTime,
    dailyDisplayEndTime,
    daysOfWeekUnavailable,
    dailyPlayLimit,
    ...restDraft
  } = baseDraft;

  const wofGameConfig = activityFormData?.displayConfig?.gameConfig;
  const displayConfig = {
    ...(dailyDisplayStartTime && {startTime: dailyDisplayStartTime}),
    ...(dailyDisplayEndTime && {endTime: dailyDisplayEndTime}),
    ...(Array.isArray(daysOfWeekUnavailable) &&
      daysOfWeekUnavailable.length > 0 && {
        daysOfTheWeekUnavailable: daysOfWeekUnavailable
      }),
    ...(dailyPlayLimit != null && {dailyLimit: dailyPlayLimit}),
    ...(hasAnyNonEmptyValue(activityFormData?.displayConfig?.asset) && {
      asset: activityFormData.displayConfig.asset
    }),
    gameConfig: {
      budget: {
        ...(budgetPeriod && {frequency: budgetPeriod.toUpperCase()}),
        ...(pointsEarned != null && {
          amount: Number.parseInt(pointsEarned, 10) || 0
        })
      },
      ...(wofGameConfig?.equalProbabilityPerSlice !== undefined && {
        equalProbabilityPerSlice: wofGameConfig.equalProbabilityPerSlice
      }),
      ...(wofGameConfig?.equalPointsPerSlice !== undefined && {
        equalPointsPerSlice: wofGameConfig.equalPointsPerSlice
      }),
      ...(Array.isArray(wofGameConfig?.wheelSlices) && {
        wheelSlices: wofGameConfig.wheelSlices
      })
    }
  };

  const wofDraft = {...restDraft, displayConfig};
  if (hasAnyNonEmptyValue(activityFormData?.translations)) {
    wofDraft.translations = activityFormData.translations;
  }

  const quizQuestions = buildQuizQuestionsList(
    activityFormData?.wofQuizQuestions
  );
  if (quizQuestions.length > 0) {
    wofDraft.quizQuestions = quizQuestions;
  }

  delete wofDraft.mediaUrls;
  delete wofDraft.wheelOfFortuneDisplayData;
  delete wofDraft.wheelSlices;
  delete wofDraft.gameImageUri;

  return wofDraft;
};
